import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { User, LoginCredentials, RegisterData, BillingInfo } from '@/types';
import { authApiService } from '@/services/api/auth';
import { useNotifications } from '@/composables/useNotifications';
import { getErrorMessage, logError, normalizeError } from '@/utils/errors';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const token = ref<string | null>(localStorage.getItem('auth_token'));
  const isLoading = ref(false);
  
  const { showNotification } = useNotifications();

  const isAuthenticated = computed(() => !!token.value && !!user.value);

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      isLoading.value = true;
      const response = await authApiService.login(credentials);
      
      user.value = response.user;
      token.value = response.token;
      localStorage.setItem('auth_token', response.token);
      
      showNotification(`¡Bienvenido ${response.user.firstName}!`, 'success');
      
      // Track login event
      if (window.trackCustomEvent) {
        window.trackCustomEvent('Auth', 'Login', {
          userId: response.user.id,
          method: 'email'
        });
      }
      
      return true;
    } catch (error: any) {
      const normalized = normalizeError(error);
      logError('Login error', error);
      
      // Handle specific Laravel validation errors
      if (normalized.status === 422) {
        const errors = (normalized.data as any)?.errors;
        if (errors?.email) {
          showNotification(errors.email[0], 'error');
        } else if (errors?.password) {
          showNotification(errors.password[0], 'error');
        } else {
          showNotification('Credenciales inválidas', 'error');
        }
      } else if (normalized.status === 401) {
        showNotification('Credenciales incorrectas', 'error');
      } else {
        showNotification(getErrorMessage(error, 'Error al iniciar sesión'), 'error');
      }
      
      // Track login error
      if (window.trackError) {
        window.trackError(error, {
          component: 'AuthStore',
          action: 'login'
        });
      }
      
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const register = async (userData: RegisterData): Promise<boolean> => {
    try {
      isLoading.value = true;
      const response = await authApiService.register(userData);
      
      user.value = response.user;
      token.value = response.token;
      localStorage.setItem('auth_token', response.token);
      
      showNotification(`¡Bienvenido ${response.user.firstName}! Tu cuenta ha sido creada exitosamente.`, 'success');
      
      // Track registration event
      if (window.trackCustomEvent) {
        window.trackCustomEvent('Auth', 'Register', {
          userId: response.user.id,
          method: 'email'
        });
      }
      
      return true;
    } catch (error: any) {
      const normalized = normalizeError(error);
      logError('Registration error', error);
      
      // Handle Laravel validation errors
      if (normalized.status === 422) {
        const errors = (normalized.data as any)?.errors;
        if (errors?.email) {
          showNotification(errors.email[0], 'error');
        } else if (errors?.password) {
          showNotification(errors.password[0], 'error');
        } else if (errors?.name) {
          showNotification(errors.name[0], 'error');
        } else {
          showNotification('Por favor verifica los datos ingresados', 'error');
        }
      } else {
        showNotification(getErrorMessage(error, 'Error al crear la cuenta'), 'error');
      }
      
      // Track registration error
      if (window.trackError) {
        window.trackError(error, {
          component: 'AuthStore',
          action: 'register'
        });
      }
      
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const socialLogin = async (provider: string, providerToken: string): Promise<boolean> => {
    try {
      isLoading.value = true;
      const response = await authApiService.socialLogin(provider, providerToken);
      
      user.value = response.user;
      token.value = response.token;
      localStorage.setItem('auth_token', response.token);
      
      showNotification(`¡Bienvenido ${response.user.firstName}!`, 'success');
      
      // Track social login event
      if (window.trackCustomEvent) {
        window.trackCustomEvent('Auth', 'SocialLogin', {
          userId: response.user.id,
          provider: provider
        });
      }
      
      return true;
    } catch (error: any) {
      logError('Social login error', error, { provider });
      showNotification(getErrorMessage(error, `Error al iniciar sesión con ${provider}`), 'error');
      
      // Track social login error
      if (window.trackError) {
        window.trackError(error, {
          component: 'AuthStore',
          action: 'socialLogin',
          provider: provider
        });
      }
      
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint if user is authenticated
      if (token.value) {
        await authApiService.logout();
      }
    } catch (error) {
      logError('Logout error', error);
      // Continue with local logout even if API call fails
    } finally {
      // Clear local state
      user.value = null;
      token.value = null;
      localStorage.removeItem('auth_token');
      
      showNotification('Sesión cerrada correctamente', 'success');
      
      // Track logout event
      if (window.trackCustomEvent) {
        window.trackCustomEvent('Auth', 'Logout', {});
      }
    }
  };

  const loadUser = async () => {
    if (!token.value) return;
    
    try {
      isLoading.value = true;
      user.value = await authApiService.getCurrentUser();
    } catch (error: any) {
      const normalized = normalizeError(error);
      logError('Load user error', error);
      
      // If token is invalid, clear auth state
      if (normalized.status === 401) {
        user.value = null;
        token.value = null;
        localStorage.removeItem('auth_token');
      }
    } finally {
      isLoading.value = false;
    }
  };

  const updateProfile = async (userData: Partial<User>): Promise<boolean> => {
    try {
      isLoading.value = true;
      const updatedUser = await authApiService.updateProfile(userData);
      user.value = updatedUser;
      showNotification('Perfil actualizado correctamente', 'success');
      
      // Track profile update
      if (window.trackCustomEvent) {
        window.trackCustomEvent('Auth', 'ProfileUpdated', {
          userId: updatedUser.id
        });
      }
      
      return true;
    } catch (error: any) {
      const normalized = normalizeError(error);
      logError('Update profile error', error);
      
      // Handle Laravel validation errors
      if (normalized.status === 422) {
        const errors = (normalized.data as any)?.errors;
        const firstError = Object.values(errors)[0] as string[];
        showNotification(firstError[0], 'error');
      } else {
        showNotification(getErrorMessage(error, 'Error al actualizar el perfil'), 'error');
      }
      
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const updateBillingInfo = async (billingInfo: BillingInfo): Promise<boolean> => {
    try {
      isLoading.value = true;
      const updatedUser = await authApiService.updateBillingInfo(billingInfo);
      user.value = updatedUser;
      showNotification('Datos de facturación actualizados correctamente', 'success');
      
      // Track billing info update
      if (window.trackCustomEvent) {
        window.trackCustomEvent('Auth', 'BillingInfoUpdated', {
          userId: updatedUser.id,
          documentType: billingInfo.documentType
        });
      }
      
      return true;
    } catch (error: any) {
      const normalized = normalizeError(error);
      logError('Update billing info error', error);
      
      // Handle Laravel validation errors
      if (normalized.status === 422) {
        const errors = (normalized.data as any)?.errors;
        const firstError = Object.values(errors)[0] as string[];
        showNotification(firstError[0], 'error');
      } else {
        showNotification(getErrorMessage(error, 'Error al actualizar datos de facturación'), 'error');
      }
      
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const uploadAvatar = async (file: File): Promise<boolean> => {
    try {
      isLoading.value = true;
      const avatarUrl = await authApiService.uploadAvatar(file);
      
      if (user.value) {
        user.value.avatar = avatarUrl;
      }
      
      showNotification('Foto de perfil actualizada correctamente', 'success');
      
      // Track avatar upload
      if (window.trackCustomEvent) {
        window.trackCustomEvent('Auth', 'AvatarUploaded', {
          userId: user.value?.id
        });
      }
      
      return true;
    } catch (error: any) {
      logError('Upload avatar error', error);
      showNotification(getErrorMessage(error, 'Error al subir la foto de perfil'), 'error');
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    try {
      isLoading.value = true;
      await authApiService.changePassword(currentPassword, newPassword);
      showNotification('Contraseña cambiada correctamente', 'success');
      
      // Track password change
      if (window.trackCustomEvent) {
        window.trackCustomEvent('Auth', 'PasswordChanged', {
          userId: user.value?.id
        });
      }
      
      return true;
    } catch (error: any) {
      const normalized = normalizeError(error);
      logError('Change password error', error);
      
      // Handle Laravel validation errors
      if (normalized.status === 422) {
        const errors = (normalized.data as any)?.errors;
        if (errors?.current_password) {
          showNotification('La contraseña actual es incorrecta', 'error');
        } else if (errors?.password) {
          showNotification(errors.password[0], 'error');
        } else {
          showNotification('Error en los datos proporcionados', 'error');
        }
      } else {
        showNotification(getErrorMessage(error, 'Error al cambiar la contraseña'), 'error');
      }
      
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    user: computed(() => user.value),
    token: computed(() => token.value),
    isLoading: computed(() => isLoading.value),
    isAuthenticated,
    login,
    register,
    socialLogin,
    logout,
    loadUser,
    updateProfile,
    updateBillingInfo,
    uploadAvatar,
    changePassword,
  };
});
